import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { AppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiZap, FiAward, FiShield, FiDribbble, FiStar } = FiIcons;

const stripHtml = (html) => html ? html.replace(/<[^>]*>?/gm, '') : '';

const Teams = () => {
  const { teams, RenderName, textStyles, gapCaptainRoster } = useContext(AppContext);

  return (
    <div className="py-12 space-y-20 max-w-7xl mx-auto px-4">
      <div className="border-b-4 border-white pb-8">
        <h1 className="font-league italic leading-none tracking-tighter uppercase dyn-rostersTitle pr-2">
          {textStyles.rostersTitle?.text}
        </h1>
      </div>

      <div className="space-y-32">
        {teams.map((team, tIdx) => {
          const isAlpha = tIdx === 0;
          const captainObj = team.members?.[team.captain_index];
          const captainName = captainObj?.name || '';

          // Filtriamo il capitano principale e ordiniamo in modo che il Capitano Basket sia il primo della lista
          const otherMembers = (team.members || [])
            .filter((_, i) => i !== team.captain_index)
            .sort((a, b) => {
              if (a.is_basket_captain && !b.is_basket_captain) return -1;
              if (!a.is_basket_captain && b.is_basket_captain) return 1;
              return 0;
            });

          // Creiamo il testo di background dinamico pulendo l'HTML e aggiungendo underscore
          const bgText = stripHtml(team.name).replace(/\s+/g, '_').toUpperCase();

          return (
            <motion.div 
              key={team.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -top-10 left-0 w-full overflow-hidden pointer-events-none select-none z-0">
                <span className={`font-league text-[15rem] md:text-[22rem] italic leading-none opacity-5 block whitespace-nowrap tracking-tighter ${isAlpha ? 'text-nba-red' : 'text-nba-blue'}`}>
                  {bgText}
                </span>
              </div>

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                <div className="lg:col-span-4">
                  <div className={`p-8 border-l-8 ${isAlpha ? 'border-nba-red bg-nba-red/5' : 'border-nba-blue bg-nba-blue/5'} backdrop-blur-sm sticky top-28`}>
                    <div className="flex items-center gap-3 mb-4">
                      <SafeIcon icon={FiShield} className={`text-2xl ${isAlpha ? 'text-nba-red' : 'text-nba-blue'}`} />
                      <span className="text-[10px] font-black uppercase tracking-[0.4em] text-white/40">
                        {team.division}
                      </span>
                    </div>
                    <h2 className="font-league text-6xl md:text-8xl text-white leading-none tracking-tight italic uppercase mb-6 pr-2">
                      <RenderName name={team.name} />
                    </h2>
                    <div className="h-1 w-20 bg-white/20 mb-8"></div>
                    <p className="text-white/50 italic text-lg leading-relaxed">{team.description}</p>
                  </div>
                </div>

                <div className="lg:col-span-8">
                  {captainName && (
                    <div className="group relative">
                      <div className={`absolute -inset-0.5 bg-gradient-to-r ${isAlpha ? 'from-nba-red to-transparent' : 'from-nba-blue to-transparent'} opacity-30 group-hover:opacity-70 transition duration-500`}></div>
                      <div className="relative bg-nba-card border border-white/10 p-6 md:p-8 flex flex-col sm:flex-row items-center text-center sm:text-left gap-8 overflow-hidden shadow-2xl">
                        <div className={`w-32 h-32 shrink-0 flex items-center justify-center rounded-none border-4 ${isAlpha ? 'border-nba-red' : 'border-nba-blue'} bg-nba-dark z-10 overflow-hidden shadow-[0_0_20px_rgba(255,255,255,0.1)]`}>
                          {captainObj?.image ? (
                            <img src={captainObj.image} alt={captainName} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                          ) : (
                            <SafeIcon icon={FiAward} className={`text-5xl ${isAlpha ? 'text-nba-red' : 'text-nba-blue'}`} />
                          )}
                        </div>
                        <div className="z-10 flex-grow">
                          {/* RUOLO PULITO */}
                          <div className={`text-[10px] sm:text-sm font-black uppercase tracking-[0.5em] mb-2 flex items-center justify-center sm:justify-start gap-2 ${isAlpha ? 'text-nba-red' : 'text-nba-blue'}`}>
                            <SafeIcon icon={FiZap} className="shrink-0" /> {captainObj?.role || 'Capitano'}
                          </div>
                          
                          {/* NOME PULITO */}
                          <div className="font-league text-6xl md:text-7xl text-white uppercase italic tracking-tighter drop-shadow-lg leading-none mb-4 text-center sm:text-left pr-2">
                            {!!captainObj?.number && <span className="text-white/40 mr-3">#{captainObj.number}</span>}
                            {captainName}
                          </div>

                          {/* BADGES AREA SOTTO AL NOME */}
                          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                            <div className="flex items-center gap-2 px-3 py-1 bg-black/40 border border-yellow-500/50 text-yellow-500 text-xl sm:text-2xl not-italic font-league tracking-widest shadow-[0_0_15px_rgba(234,179,8,0.2)] rounded shrink-0" title="Designated Team Captain">
                              <SafeIcon icon={FiStar} className="text-lg sm:text-xl pb-0.5" /> CAPTAIN
                            </div>
                            {captainObj?.is_basket_captain && (
                              <div className="flex items-center gap-2 px-3 py-1 bg-black/40 border border-orange-500/50 text-orange-500 text-xl sm:text-2xl not-italic font-league tracking-widest shadow-[0_0_15px_rgba(249,115,22,0.2)] rounded shrink-0" title="Basket Captain">
                                <SafeIcon icon={FiDribbble} className="text-lg sm:text-xl pb-0.5" /> BASKET
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4" style={{ marginTop: captainName ? `${gapCaptainRoster !== undefined ? gapCaptainRoster : 3}rem` : '0', marginBottom: '2rem' }}>
                    <h3 className="font-league text-3xl text-white/30 uppercase tracking-widest italic pr-2">Active Roster</h3>
                    <div className="h-px flex-grow bg-white/10"></div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {otherMembers.map((m, mIdx) => (
                      <motion.div 
                        key={mIdx}
                        whileHover={{ x: 10 }}
                        className={`flex items-center gap-6 p-4 bg-white/5 border border-white/5 border-l-4 ${isAlpha ? 'border-l-nba-red/50 hover:border-l-nba-red' : 'border-l-nba-blue/50 hover:border-l-nba-blue'} transition-all group overflow-hidden`}
                      >
                        <div className={`w-16 h-16 shrink-0 flex items-center justify-center bg-nba-dark transition-all overflow-hidden border ${m.is_basket_captain ? 'border-orange-500/50' : 'border-white/10'}`}>
                          {m.image ? (
                            <img src={m.image} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300" />
                          ) : (
                            <SafeIcon icon={FiUser} className="text-3xl text-white/10" />
                          )}
                        </div>
                        <div className="overflow-hidden w-full">
                          <div className="font-league text-4xl tracking-wide uppercase text-white/80 group-hover:text-white transition-colors truncate italic pr-2">
                            {!!m.number && <span className="text-white/40 mr-2">#{m.number}</span>}
                            {m.name}
                          </div>
                          <div className="flex items-center flex-wrap gap-2 mt-1.5">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-white/20 group-hover:text-white/40">
                              {m.role || 'Active Athlete'}
                            </span>
                            {m.is_basket_captain && (
                              <span className="text-[9px] font-bold uppercase tracking-widest text-orange-500 bg-orange-500/10 px-1.5 py-0.5 rounded-sm border border-orange-500/20 flex items-center gap-1">
                                <SafeIcon icon={FiDribbble} /> BASKET
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Teams;